import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Chart, registerables } from 'chart.js';
import type { EChartsCoreOption } from 'echarts/core';
import { PingService } from '../../ping.service';
import { OccurrenceService } from '../../core/occurrence/occurrence.service';
import {
  ORIGIN_LABELS, TYPE_ICONS, TYPE_LABELS, stateBadgeClass, stateBadgeLabel,
} from '../../core/occurrence/occurrence.labels';
import {
  Occurrence, OccurrenceOrigin, OccurrenceStatistics, OccurrenceType,
} from '../../core/occurrence/occurrence.models';
import { ThemeService } from '../../core/theme/theme.service';
import { EChart } from '../../shared/echart/echart';

Chart.register(...registerables);

type SystemStatus = 'loading' | 'online' | 'offline';

interface LineData {
  labels: string[];
  created: number[];
  resolved: number[];
}

const GRID = '#edf2f9';
const GRID_DARK = '#1e3a5c';
const AXIS = '#95aac9';

@Component({
  selector: 'app-dashboard',
  imports: [RouterLink, DatePipe, EChart],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
})
export class Dashboard implements OnInit, OnDestroy {
  private readonly pingService = inject(PingService);
  private readonly occurrences = inject(OccurrenceService);
  private readonly theme = inject(ThemeService);

  // --- Ocorrências: o assunto da casa. O atendimento vem depois, como bloco secundário.
  protected readonly occStats = signal<OccurrenceStatistics | null>(null);
  protected readonly occList = signal<Occurrence[]>([]);

  protected readonly typeIcons = TYPE_ICONS;
  protected readonly currentYear = new Date().getFullYear();

  /** As 6 mais recentes do ano, para dar entrada rápida na lista. */
  protected readonly occRecent = computed(() => this.occList().slice(0, 6));

  /** Prazos a vencer (o backend marca deadlineApproaching) — só o que ainda está em curso. */
  protected readonly occDeadlines = computed(() =>
    this.occList().filter((item) => item.deadlineApproaching && item.situation !== 'FECHADO').slice(0, 5));

  protected readonly occInTreatment = computed(() =>
    (this.occStats()?.byStateAndOrigin ?? [])
      .filter((row) => row.state === 'EM_TRATAMENTO')
      .reduce((sum, row) => sum + row.count, 0));

  /** Há algo para desenhar na linha do topo? (sem dados o Chart.js inventa um eixo de −1 a 1) */
  protected readonly hasOccurrenceSeries = computed(() => this.occList().length > 0);

  /** Badge do estado — mesma linguagem visual da lista de ocorrências. */
  protected occBadgeClass(item: Occurrence): string {
    return stateBadgeClass(item);
  }

  protected occStateLabel(item: Occurrence): string {
    return stateBadgeLabel(item);
  }

  /** Por analisar: entraram e ainda ninguém lhes pegou. */
  protected readonly occRegistadas = computed(() =>
    (this.occStats()?.byStateAndOrigin ?? [])
      .filter((row) => row.state === 'REGISTADO')
      .reduce((sum, row) => sum + row.count, 0));

  protected readonly occOnTimePct = computed(() => {
    const items = this.occStats()?.closedWithinDeadlineByOrigin ?? [];
    const total = items.reduce((sum, item) => sum + item.total, 0);
    const onTime = items.reduce((sum, item) => sum + item.onTime, 0);
    return total ? Math.round((onTime / total) * 100) : null;
  });

  /** Mini-donut: nº de ocorrências por origem (resumo do gráfico detalhado). */
  protected readonly occOriginDonut = computed<EChartsCoreOption>(() => {
    const dark = this.theme.dark();
    const colors = ['#12263f', '#2c7be5', '#39afd1', '#00b27a', '#f6c343', '#fd7e14', '#e63757', '#6e84a3'];
    const ring = dark ? '#152e4d' : '#fff';
    const data = (this.occStats()?.byOrigin ?? []).map((item, index) => ({
      name: ORIGIN_LABELS[item.key as OccurrenceOrigin] ?? item.key,
      value: item.count,
      itemStyle: {
        color: dark && colors[index % colors.length] === '#12263f' ? '#4c8fe9' : colors[index % colors.length],
        borderRadius: 6, borderColor: ring, borderWidth: 3,
      },
    }));
    return {
      tooltip: { trigger: 'item', backgroundColor: dark ? '#152e4d' : '#fff', textStyle: { color: dark ? '#d2ddec' : '#283e59' } },
      series: [{
        type: 'pie',
        radius: ['48%', '72%'],
        center: ['50%', '50%'],
        data,
        label: { color: dark ? '#d2ddec' : '#283e59', fontSize: 10, formatter: '{d}%' },
        labelLine: { length: 6, length2: 4 },
      }],
    };
  });

  /** Mini-barras: top 5 tipos de ocorrência no ano. */
  protected readonly occTypeBar = computed<EChartsCoreOption>(() => {
    const dark = this.theme.dark();
    const items = [...(this.occStats()?.byType ?? [])].sort((a, b) => b.count - a.count).slice(0, 5).reverse();
    const short = (key: string) => {
      const label = (TYPE_LABELS[key as OccurrenceType] ?? key).split(' (')[0];
      return label.length > 22 ? `${label.slice(0, 21)}…` : label;
    };
    return {
      tooltip: { trigger: 'axis', backgroundColor: dark ? '#152e4d' : '#fff', textStyle: { color: dark ? '#d2ddec' : '#283e59' } },
      grid: { left: 4, right: 26, top: 8, bottom: 4, containLabel: true },
      xAxis: {
        type: 'value',
        axisLabel: { show: false },
        splitLine: { show: false },
        minInterval: 1,
      },
      yAxis: {
        type: 'category',
        data: items.map((item) => short(item.key)),
        axisLabel: { color: dark ? '#95aac9' : '#6e84a3', fontSize: 10 },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [{
        type: 'bar',
        data: items.map((item) => item.count),
        itemStyle: { color: dark ? '#4c8fe9' : '#12263f', borderRadius: [0, 9, 9, 0] },
        barMaxWidth: 16,
        label: { show: true, position: 'right', color: dark ? '#d2ddec' : '#283e59', fontWeight: 700, fontSize: 11 },
      }],
    };
  });

  private readonly lineCanvas = viewChild<ElementRef<HTMLCanvasElement>>('lineCanvas');

  protected readonly system = signal<SystemStatus>('loading');

  private readonly lineData = signal<LineData | null>(null);



  private lineChart?: Chart;


  constructor() {
    effect(() => {
      const d = this.lineData();
      const c = this.lineCanvas();
      if (d && c) this.drawLine(c.nativeElement, d);
    });
  }

  ngOnInit(): void {
    this.checkSystem();
    this.occurrences.statistics(this.currentYear).subscribe({
      next: (stats) => this.occStats.set(stats),
      error: () => this.occStats.set(null),
    });
    // A lista alimenta a série do topo, as recentes e os prazos a vencer.
    this.occurrences.list({ year: this.currentYear, size: 500 }).subscribe({
      next: (result) => {
        this.occList.set(result.content);
        this.buildOccurrenceSeries(result.content);
      },
      error: () => this.occList.set([]),
    });
  }

  ngOnDestroy(): void {
    this.lineChart?.destroy();
  }

  protected checkSystem(): void {
    this.system.set('loading');
    this.pingService.ping().subscribe({
      next: () => this.system.set('online'),
      error: () => this.system.set('offline'),
    });
  }





  /**
   * Série do topo (últimos 21 dias): ocorrências registadas e fechadas, acumuladas.
   * O gráfico do header passou a ser sobre ocorrências — era de chamados.
   */
  private buildOccurrenceSeries(list: Occurrence[]): void {
    const days = 21;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today);
    start.setDate(today.getDate() - (days - 1));

    const labels: string[] = [];
    const created: number[] = [];
    const closed: number[] = [];
    for (let i = 0; i < days; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      day.setHours(23, 59, 59, 999);
      labels.push(`${String(day.getDate()).padStart(2, '0')}/${String(day.getMonth() + 1).padStart(2, '0')}`);
      created.push(list.filter((o) => new Date(o.occurrenceDate) <= day).length);
      closed.push(list.filter((o) => o.closingDate != null && new Date(o.closingDate) <= day).length);
    }
    this.lineData.set({ labels, created, resolved: closed });
  }


  /** Base comum dos tooltips (hints): radius, padding, point-style e fontes. */
  private tooltipBase() {
    return {
      borderWidth: 1,
      cornerRadius: 12,
      padding: 12,
      boxPadding: 6,
      usePointStyle: true,
      titleFont: { size: 12, weight: 600 },
      bodyFont: { size: 12.5 },
      titleMarginBottom: 6,
      caretSize: 6,
      caretPadding: 8,
    };
  }

  /** Tooltip do gráfico de linha (sempre sobre o header navy): chip claro. */
  private tooltipOnDark() {
    return {
      ...this.tooltipBase(),
      backgroundColor: '#ffffff',
      titleColor: '#12263f',
      bodyColor: '#3b506c',
      borderColor: 'rgba(18, 38, 63, 0.08)',
    };
  }


  private drawLine(canvas: HTMLCanvasElement, d: LineData): void {
    this.lineChart?.destroy();
    this.lineChart = new Chart(canvas, {
      type: 'line',
      data: {
        labels: d.labels,
        datasets: [
          { label: 'Registadas', data: d.created, borderColor: '#ffffff', borderWidth: 3, tension: 0.4, pointRadius: 0, fill: false },
          { label: 'Fechadas', data: d.resolved, borderColor: '#00d97e', borderWidth: 3, tension: 0.4, pointRadius: 0, fill: false },
        ],
      },
      options: {
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { mode: 'index', intersect: false, ...this.tooltipOnDark() } },
        scales: {
          x: { grid: { display: false }, border: { display: false }, ticks: { color: AXIS, maxTicksLimit: 7 } },
          y: { grid: { color: GRID_DARK }, border: { display: false }, ticks: { color: AXIS, padding: 10, precision: 0 } },
        },
      },
    });
  }


}
